package org.teddapp.project.ui.mindmap

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.tedd.db.Mind_map
import com.tedd.db.Mind_map_node
import com.tedd.db.Mind_map_node_note
import com.tedd.db.Note
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.launch
import org.teddapp.project.db.ParentKind
import org.teddapp.project.db.TeddRepositories
import org.teddapp.project.db.repo.MindMapRepository
import org.teddapp.project.ui.nav.NavIntent

/**
 * L'état de l'écran Cartes heuristiques : la liste des cartes, la carte ouverte, son
 * arbre, ce qui est sélectionné, replié, en édition, cherché. Les actions délèguent au
 * dépôt ; les flux de la base tiennent l'arbre à jour, y compris quand un autre
 * appareil le modifie.
 *
 * Le pliage est propre à l'appareil : il se garde dans les préférences locales, pas
 * dans la base synchronisée (se tester sur le téléphone ne replie pas le bureau).
 */
class MindMapModel(
    private val repos: TeddRepositories,
    private val scope: CoroutineScope,
) {
    private val repo: MindMapRepository get() = repos.mindMaps

    // ── Liste des cartes ──────────────────────────────────────────────────────
    var maps by mutableStateOf<List<Mind_map>>(emptyList())
        private set
    var creating by mutableStateOf(false)

    // ── Carte ouverte ─────────────────────────────────────────────────────────
    var openMapId by mutableStateOf<String?>(null)
        private set
    var map by mutableStateOf<Mind_map?>(null)
        private set
    var nodes by mutableStateOf<List<Mind_map_node>>(emptyList())
        private set
    /** Liens vers des notes de la bibliothèque, par nœud. */
    var linksByNode by mutableStateOf<Map<String, List<Mind_map_node_note>>>(emptyMap())
        private set
    /** Nombre de notes (écrites dans le nœud + rattachées) par nœud : l'icône sur la boîte. */
    var noteCounts by mutableStateOf<Map<String, Int>>(emptyMap())
        private set
    /** Toutes les notes vivantes, pour le sélecteur de rattachement et les notes internes. */
    var allNotes by mutableStateOf<List<Note>>(emptyList())
        private set
    var notebooks by mutableStateOf<List<com.tedd.db.Notebook>>(emptyList())
        private set
    var skills by mutableStateOf<List<com.tedd.db.Skill>>(emptyList())
        private set
    var objectives by mutableStateOf<List<com.tedd.db.Objective>>(emptyList())
        private set

    var collapsed by mutableStateOf<Set<String>>(emptySet())
        private set
    var selectedId by mutableStateOf<String?>(null)
    var editingId by mutableStateOf<String?>(null)
    /** Le panneau des notes du nœud sélectionné est ouvert (feuille sur téléphone). */
    var notesOpen by mutableStateOf(false)
    var search by mutableStateOf("")
    var searchOpen by mutableStateOf(false)
    /** Mode révision : on révèle les branches une à une. */
    var revision by mutableStateOf(false)
    /** Nœuds ajoutés par « Mettre à jour depuis la compétence », surlignés un instant. */
    var freshIds by mutableStateOf<Set<String>>(emptySet())
    /** La dernière suppression, pour « Annuler ». */
    var lastDeletion by mutableStateOf<MindMapRepository.Deletion?>(null)
    /** La disposition courante, posée par le canevas ; sert au clavier et au centrage. */
    var layout by mutableStateOf(MindMapLayoutResult.Empty)
    /** Demande au canevas de centrer la vue sur ce nœud (consommée par lui). */
    var centerRequest by mutableStateOf<String?>(null)
    /** Demande au canevas d'ajuster la vue à toute la carte. */
    var fitRequest by mutableStateOf(0)

    private var mapJob: Job? = null

    init {
        scope.launch { repo.observeAll().collect { maps = it } }
        scope.launch { repos.notes.observeAll().collect { allNotes = it; recount() } }
        scope.launch { repos.notebooks.observeAll().collect { notebooks = it } }
        scope.launch { repos.skills.observeAll().collect { skills = it } }
        scope.launch { repos.objectives.observeAll().collect { objectives = it } }
        NavIntent.consumeMindMap()?.let { open(it) }
        if (NavIntent.consumeCreateMindMap()) creating = true
    }

    val root: Mind_map_node? get() = nodes.firstOrNull { it.parent_id == null }
    val selected: Mind_map_node? get() = selectedId?.let { id -> nodes.firstOrNull { it.id == id } }

    /** Les nœuds dont le texte contient la recherche. */
    val matches: Set<String>
        get() {
            val q = search.trim()
            if (q.isEmpty()) return emptySet()
            return nodes.filter { it.text.contains(q, ignoreCase = true) }.map { it.id }.toSet()
        }

    fun layoutNodes(): List<LayoutNode> = nodes.map { LayoutNode(it.id, it.parent_id, it.order_index, it.text, it.created_at) }

    // ── Ouvrir, fermer ────────────────────────────────────────────────────────

    fun open(mapId: String) {
        if (openMapId == mapId) return
        close()
        openMapId = mapId
        mapJob = scope.launch {
            launch { repo.observeById(mapId).collect { map = it } }
            launch {
                combine(repo.observeNodes(mapId), repo.observeLinks(mapId)) { n, l -> n to l }.collect { (n, l) ->
                    nodes = n
                    linksByNode = l.groupBy { it.node_id }
                    recount()
                    val ids = n.map { it.id }.toSet()
                    if (selectedId != null && selectedId !in ids) { selectedId = null; editingId = null }
                    if (collapsed.any { it !in ids }) collapsed = collapsed.filterTo(HashSet()) { it in ids }
                }
            }
            launch {
                val saved = repos.preferences.get(collapsedKey(mapId))
                if (saved != null) {
                    collapsed = saved.split(',').filter { it.isNotBlank() }.toSet()
                } else {
                    // Première ouverture d'une grande carte : repliée au niveau 1, sinon elle
                    // s'affiche minuscule et illisible. L'utilisateur déplie ce qu'il veut.
                    val all = repo.nodes(mapId)
                    if (all.size > BIG_MAP) {
                        // Visibles : la racine, ses branches et leurs enfants ; replié au-delà.
                        val rootId = all.firstOrNull { it.parent_id == null }?.id
                        val parents = all.mapNotNull { it.parent_id }.toSet()
                        val level1 = all.filter { it.parent_id == rootId }.map { it.id }.toSet()
                        val level2 = all.filter { it.parent_id in level1 }.map { it.id }.toSet()
                        applyCollapsed(level2.filter { it in parents }.toSet())
                    }
                }
            }
        }
        fitRequest++
    }

    fun close() {
        mapJob?.cancel()
        mapJob = null
        openMapId = null
        map = null
        nodes = emptyList()
        linksByNode = emptyMap()
        collapsed = emptySet()
        selectedId = null
        editingId = null
        notesOpen = false
        search = ""
        searchOpen = false
        revision = false
        layout = MindMapLayoutResult.Empty
    }

    private fun recount() {
        val inline = allNotes.filter { it.parent_kind == ParentKind.MindMapNode.raw && it.parent_id != null }.groupingBy { it.parent_id!! }.eachCount()
        val out = HashMap<String, Int>()
        for ((id, n) in inline) out[id] = n
        for ((id, l) in linksByNode) out[id] = (out[id] ?: 0) + l.size
        noteCounts = out
    }

    // ── Cartes ────────────────────────────────────────────────────────────────

    fun create(title: String, skillId: String? = null, objectiveId: String? = null, onDone: (String) -> Unit = {}) {
        val clean = title.trim().ifEmpty { "Nouvelle carte" }
        scope.launch {
            val id = repo.create(clean, skillId, objectiveId)
            open(id)
            selectedId = root?.id
            onDone(id)
        }
    }

    /** Une carte prête à remplir : le sujet et quelques branches à renommer. */
    fun createFromTemplate(title: String, branches: List<String>) {
        scope.launch {
            val id = repo.create(title)
            val rootId = repo.nodes(id).first { it.parent_id == null }.id
            for (b in branches) repo.addChild(rootId, b)
            open(id)
        }
    }

    fun deleteMap(mapId: String) {
        scope.launch {
            if (openMapId == mapId) close()
            repo.deleteMap(mapId)
        }
    }

    fun setAttachments(skillId: String?, objectiveId: String?) {
        val id = openMapId ?: return
        scope.launch { repo.setAttachments(id, skillId, objectiveId) }
    }

    fun refreshFromSkill(onDone: (Int) -> Unit) {
        val id = openMapId ?: return
        scope.launch {
            val before = nodes.map { it.id }.toSet()
            val added = repo.refreshFromSkill(id)
            if (added > 0) {
                val after = repo.nodes(id)
                freshIds = after.map { it.id }.filter { it !in before }.toSet()
            }
            onDone(added)
        }
    }

    // ── Nœuds ─────────────────────────────────────────────────────────────────

    fun select(id: String?) {
        if (editingId != null && editingId != id) editingId = null
        selectedId = id
    }

    fun startEdit(id: String) {
        selectedId = id
        editingId = id
    }

    /** Valide le texte en édition ; un nœud laissé vide (hors racine) est retiré. */
    fun commitEdit(id: String, text: String) {
        val node = nodes.firstOrNull { it.id == id } ?: return
        editingId = null
        val clean = text.trim()
        scope.launch {
            if (clean.isEmpty() && node.parent_id != null) {
                repo.deleteNode(id)
                if (selectedId == id) selectedId = node.parent_id
            } else if (clean.isNotEmpty()) {
                repo.rename(id, clean)
            }
        }
    }

    /**
     * Ferme le panneau des notes. Un nœud resté vide, sans enfant ni note, est retiré :
     * sur téléphone, c'est l'équivalent de quitter l'édition en place sans rien écrire.
     */
    fun closeNotes() {
        notesOpen = false
        editingId = null
        val id = selectedId ?: return
        val node = nodes.firstOrNull { it.id == id } ?: return
        if (node.parent_id != null && node.text.isBlank() && nodes.none { it.parent_id == id } && (noteCounts[id] ?: 0) == 0) {
            scope.launch { repo.deleteNode(id); if (selectedId == id) selectedId = node.parent_id }
        }
    }

    /** Renomme au fil de la frappe depuis le panneau ; un texte vide est ignoré (pas de suppression ici). */
    fun renameLive(id: String, text: String) {
        if (text.isBlank()) return
        scope.launch { repo.rename(id, text) }
    }

    fun updateNote(note: Note, title: String?, body: String) {
        scope.launch {
            repos.notes.update(
                id = note.id, parentKind = ParentKind.fromRaw(note.parent_kind) ?: ParentKind.Standalone, parentId = note.parent_id,
                title = title?.takeIf { it.isNotBlank() }, body = body, color = note.color,
                isPinned = note.is_pinned == 1L, isFavorite = note.is_favorite == 1L, tags = note.tags,
                orderIndex = note.order_index, attachments = note.attachments,
            )
        }
    }

    fun deleteNote(noteId: String) {
        scope.launch { repos.notes.softDelete(noteId) }
    }

    fun bookName(id: String?): String? = id?.let { b -> notebooks.firstOrNull { it.id == b }?.name }

    fun addChild(parent: String? = null) {
        val parentId = parent ?: selectedId ?: root?.id ?: return
        scope.launch {
            // Un parent replié se déplie : on doit voir ce qu'on ajoute.
            if (parentId in collapsed) applyCollapsed(collapsed - parentId)
            val id = repo.addChild(parentId, "") ?: return@launch
            selectedId = id
            editingId = id
            centerRequest = id
        }
    }

    fun addSibling(node: String? = null) {
        val nodeId = node ?: selectedId ?: return
        scope.launch {
            val id = repo.addSibling(nodeId, "") ?: return@launch
            selectedId = id
            editingId = id
            centerRequest = id
        }
    }

    fun moveAmongSiblings(delta: Int, node: String? = null) {
        val nodeId = node ?: selectedId ?: return
        scope.launch { repo.moveAmongSiblings(nodeId, delta) }
    }

    fun reparent(nodeId: String, newParentId: String, onRefused: () -> Unit = {}) {
        scope.launch { if (!repo.reparent(nodeId, newParentId)) onRefused() }
    }

    /** Ce que la suppression emporterait, pour décider s'il faut confirmer. */
    fun deletionWeight(nodeId: String): Int {
        val childrenOf = nodes.groupBy { it.parent_id }
        var count = 0
        val stack = ArrayDeque(listOf(nodeId))
        while (stack.isNotEmpty()) {
            val id = stack.removeLast()
            count += noteCounts[id] ?: 0
            childrenOf[id]?.forEach { count++; stack.addLast(it.id) }
        }
        return count
    }

    fun deleteNode(node: String? = null, onDone: (MindMapRepository.Deletion) -> Unit = {}) {
        val nodeId = node ?: selectedId ?: return
        val node = nodes.firstOrNull { it.id == nodeId } ?: return
        if (node.parent_id == null) return
        scope.launch {
            val deletion = repo.deleteNode(nodeId)
            if (!deletion.isEmpty) {
                lastDeletion = deletion
                if (selectedId == nodeId) selectedId = node.parent_id
                editingId = null
                onDone(deletion)
            }
        }
    }

    fun undoDeletion() {
        val d = lastDeletion ?: return
        lastDeletion = null
        scope.launch {
            repo.restore(d)
            selectedId = d.nodeIds.firstOrNull()
        }
    }

    fun setNodeColor(nodeId: String, color: String?) {
        scope.launch { repo.setNodeColor(nodeId, color) }
    }

    // ── Notes ─────────────────────────────────────────────────────────────────

    fun inlineNotes(nodeId: String): List<Note> =
        allNotes.filter { it.parent_kind == ParentKind.MindMapNode.raw && it.parent_id == nodeId }

    fun attachedNotes(nodeId: String): List<Note> {
        val ids = linksByNode[nodeId].orEmpty().map { it.note_id }
        val byId = allNotes.associateBy { it.id }
        return ids.mapNotNull { byId[it] }.distinctBy { it.id }
    }

    fun newInlineNote(nodeId: String, onDone: (String) -> Unit = {}) {
        scope.launch { onDone(repos.notes.create(ParentKind.MindMapNode, body = "", parentId = nodeId)) }
    }

    fun attachNote(nodeId: String, noteId: String) {
        scope.launch { repo.attachNote(nodeId, noteId) }
    }

    fun detachNote(nodeId: String, noteId: String) {
        scope.launch { repo.detachNote(nodeId, noteId) }
    }

    // ── Pliage ────────────────────────────────────────────────────────────────

    private fun collapsedKey(mapId: String) = "mindmap.collapsed.$mapId"

    fun applyCollapsed(value: Set<String>) {
        collapsed = value
        val mapId = openMapId ?: return
        scope.launch { repos.preferences.set(collapsedKey(mapId), value.joinToString(",")) }
    }

    fun toggleCollapse(node: String? = null) {
        val nodeId = node ?: selectedId ?: return
        if (nodes.none { it.parent_id == nodeId }) return
        applyCollapsed(if (nodeId in collapsed) collapsed - nodeId else collapsed + nodeId)
    }

    /** Replie tout ce qui a des enfants, sauf la racine. */
    fun collapseAll() {
        val parents = nodes.mapNotNull { it.parent_id }.toSet()
        applyCollapsed(parents - (root?.id ?: ""))
    }

    fun expandAll() = applyCollapsed(emptySet())

    /** Ne laisse visibles que les branches de niveau 1 : le point de départ d'une révision. */
    fun collapseToLevel1() {
        val rootId = root?.id ?: return
        val parents = nodes.mapNotNull { it.parent_id }.toSet()
        applyCollapsed(nodes.filter { it.parent_id == rootId && it.id in parents }.map { it.id }.toSet() + (parents - rootId - nodes.filter { it.parent_id == rootId }.map { it.id }.toSet()))
    }

    /** Déplie les ancêtres d'un nœud, pour le rendre visible. */
    fun reveal(nodeId: String) {
        val byId = nodes.associateBy { it.id }
        val toOpen = HashSet<String>()
        var current = byId[nodeId]?.parent_id
        while (current != null) { toOpen += current; current = byId[current]?.parent_id }
        if (toOpen.any { it in collapsed }) applyCollapsed(collapsed - toOpen)
    }

    /** En révision : déplie le nœud sélectionné d'un niveau. */
    fun revealNext() {
        val id = selectedId ?: root?.id ?: return
        if (id in collapsed) { applyCollapsed(collapsed - id); return }
        // Déjà déplié : on révèle le premier enfant encore replié.
        val next = nodes.filter { it.parent_id == id }.firstOrNull { it.id in collapsed } ?: return
        applyCollapsed(collapsed - next.id)
    }

    /** Nœuds visibles / total, pour le compteur de révision. */
    val revealedCount: Int get() = layout.nodes.size
    val totalCount: Int get() = nodes.size

    // ── Recherche et navigation ───────────────────────────────────────────────

    fun nextMatch() {
        val ordered = layout.nodes.map { it.id }.filter { it in matches }.ifEmpty { matches.toList() }
        if (ordered.isEmpty()) return
        val i = ordered.indexOf(selectedId)
        val next = ordered[(i + 1) % ordered.size]
        reveal(next)
        selectedId = next
        centerRequest = next
    }

    fun selectParent() {
        val id = selectedId ?: return
        layout.parentOf[id]?.let { selectedId = it; centerRequest = it }
    }

    fun selectFirstChild() {
        val id = selectedId ?: root?.id ?: return
        if (id in collapsed) { applyCollapsed(collapsed - id) }
        val kids = nodes.filter { it.parent_id == id }.sortedBy { it.order_index }
        kids.firstOrNull()?.let { selectedId = it.id; centerRequest = it.id }
    }

    fun selectSibling(delta: Int) {
        val id = selectedId ?: return
        val node = nodes.firstOrNull { it.id == id } ?: return
        val siblings = nodes.filter { it.parent_id == node.parent_id }.sortedBy { it.order_index }
        val i = siblings.indexOfFirst { it.id == id }
        val target = siblings.getOrNull(i + delta)
            // Plus de frère : le nœud visible suivant dans l'ordre d'affichage.
            ?: layout.nodes.getOrNull(layout.nodes.indexOfFirst { it.id == id } + delta)?.node?.let { n -> nodes.firstOrNull { it.id == n.id } }
            ?: return
        selectedId = target.id
        centerRequest = target.id
    }

    /** ← et → dépendent du côté de la branche : vers l'extérieur, c'est vers les enfants. */
    fun selectHorizontal(toRight: Boolean) {
        val id = selectedId ?: root?.id ?: return
        val placed = layout.byId[id]
        val outward = placed == null || placed.depth == 0 || (placed.side == LayoutSide.Right) == toRight
        if (outward) selectFirstChild() else selectParent()
    }
}

/** Au-delà, une carte s'ouvre repliée au niveau 1 la première fois. */
private const val BIG_MAP = 40

@Composable
fun rememberMindMapModel(repos: TeddRepositories): MindMapModel {
    val scope = rememberCoroutineScope()
    return remember(repos) { MindMapModel(repos, scope) }
}
